import { getAuthToken } from '../utils/auth';
const DEFAULT_BASE_URL = '/api/places';
const DEFAULT_LIMIT = 8;

function normalizeCountryCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw.toUpperCase();

  if (normalized === 'UK') return 'GB';
  if (normalized === 'EL') return 'GR';

  return /^[A-Z]{2}$/.test(normalized) ? normalized : '';
}

function buildHeaders(extra = {}) {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function normalizePlace(raw = {}) {
  const primaryName = String(raw.primaryName || raw.primary_name || raw.name || '').trim();
  const city = String(raw.city || '').trim();
  const district = String(raw.district || raw.suburb || '').trim();
  const country = String(raw.country || '').trim();
  const countryCode = normalizeCountryCode(raw.countryCode || raw.country_code);
  const region = String(raw.region || raw.state || '').trim();
  const label = String(
    raw.label ||
      [primaryName || district || city, city && city !== primaryName ? city : '', region, country]
        .filter(Boolean)
        .join(', ') ||
      primaryName ||
      district ||
      city ||
      country
  ).trim();

  return {
    primaryName,
    city,
    district,
    country,
    countryCode,
    region,
    label,
    lat: raw.lat == null ? null : Number(raw.lat),
    lng: raw.lng == null ? null : Number(raw.lng),
    timezoneOffsetMinutes:
      raw.timezoneOffsetMinutes == null ? null : Number(raw.timezoneOffsetMinutes),
    birthTimezone: typeof raw.birthTimezone === 'string' ? raw.birthTimezone : null,
    source: raw.source || null,
  };
}

async function request(path, options = {}) {
  const response = await fetch(path, {
    credentials: 'include',
    ...options,
    headers: buildHeaders(options.headers || {}),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message =
      payload?.error ||
      payload?.detail ||
      payload?.message ||
      `Place request failed with status ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

export async function searchPlaces(query, opts = {}) {
  const q = String(query || '').trim();
  if (q.length < 2) return [];

  const limit = Number.isFinite(Number(opts.limit)) ? Number(opts.limit) : DEFAULT_LIMIT;
  const baseUrl = opts.baseUrl || DEFAULT_BASE_URL;
  // Birth-place search is global by default. A viewer's detected country is
  // not evidence about the birthplace being searched and must not filter out
  // valid international results. Callers may still provide an explicit
  // country/countryCode when they intentionally want a scoped search.
  const preferredCountryCode = normalizeCountryCode(
    opts.country || opts.countryCode || opts.preferredCountryCode
  );

  const params = new URLSearchParams({
    q,
    limit: String(limit),
  });

  if (preferredCountryCode) {
    params.set('country', preferredCountryCode);
  }

  const url = `${baseUrl}/search?${params.toString()}`;

  const payload = await request(url, { method: 'GET' });
  const list = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.data?.results)
      ? payload.data.results
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  return list.map(normalizePlace).filter((item) => item.label);
}

export function hydrateBirthLocation(person = {}, place = {}) {
  const resolvedCity =
    place.primaryName ||
    place.district ||
    place.city ||
    person.birthCity ||
    '';

  const resolvedLabel =
    place.label ||
    [resolvedCity, place.city && place.city !== resolvedCity ? place.city : '', place.country]
      .filter(Boolean)
      .join(', ');

  return {
    ...person,
    birthCity: resolvedCity,
    birthCountry: place.country || person.birthCountry || '',
    birthCountryCode: place.countryCode || person.birthCountryCode || '',
    birthPlaceLabel: resolvedLabel || person.birthPlaceLabel || '',
    lat:
      place.lat == null || Number.isNaN(Number(place.lat))
        ? person.lat || ''
        : String(Number(place.lat)),
    lng:
      place.lng == null || Number.isNaN(Number(place.lng))
        ? person.lng || ''
        : String(Number(place.lng)),
    // Provider offsets describe the lookup response, not necessarily the historical
    // offset at birth. Keep disambiguation explicit and let the backend derive it.
    timezoneOffsetMinutes: '',
    birthTimezone: place.birthTimezone || '',
  };
}

const placeApi = {
  searchPlaces,
  hydrateBirthLocation,
};

export default placeApi;
